import { AnyAction, Reducer } from "redux";
import { StoreError } from "../errors";
import { readResource } from "./resource-read";

/**
 * State shape shared by every "load this resource by id, subscribe to changes,
 * and surface loading/error per id" slice (answers, results, per-event users, …).
 *
 * - `itemsById`: latest known value, keyed by id. Survives across switches so a
 *   re-visit can render immediately while a refresh runs in the background.
 * - `loadingFor`: which ids currently have an in-flight fetch.
 * - `errorFor`: last error per id (clears on next successful load).
 * - `requestToken`: monotonically-increasing counter per id. Bumped at the
 *   start of every fetch; the fetch only commits if the token still matches
 *   when the response arrives. This is the staleness guard that prevents an
 *   in-flight A → B → A switch from leaving the screen showing B's data.
 */
export type IdResourceState<T> = {
  itemsById: Record<string, T>;
  loadingFor: Record<string, boolean>;
  errorFor: Record<string, StoreError | null>;
  requestToken: Record<string, number>;
};

export const initialIdResourceState = <T>(): IdResourceState<T> => ({
  itemsById: {},
  loadingFor: {},
  errorFor: {},
  requestToken: {},
});

export type IdResourceActionTypes = {
  /** Fetch started for a given id; bumps requestToken[id]. */
  startedLoading: string;
  /** Fetch completed; payload carries the value to commit. */
  loaded: string;
  /** Fetch failed. */
  errored: string;
  /** External listener removed an item (e.g. doc deleted). */
  removed: string;
};

/**
 * Build a slice-scoped action-type bundle. The `prefix` should be unique per
 * slice (`"answers"`, `"results"`, …) so action types don't collide.
 */
export const buildIdResourceActionTypes = (
  prefix: string
): IdResourceActionTypes => ({
  startedLoading: `${prefix}/started_loading`,
  loaded: `${prefix}/loaded`,
  errored: `${prefix}/errored`,
  removed: `${prefix}/removed`,
});

export type IdResourceActions<T> = {
  startedLoading: (id: string, requestToken: number) => AnyAction;
  loaded: (id: string, item: T, requestToken: number) => AnyAction;
  errored: (id: string, error: StoreError, requestToken: number) => AnyAction;
  removed: (id: string) => AnyAction;
};

export const buildIdResourceActions = <T>(
  types: IdResourceActionTypes
): IdResourceActions<T> => ({
  startedLoading: (id, requestToken) => ({
    type: types.startedLoading,
    payload: { id, requestToken },
  }),
  loaded: (id, item, requestToken) => ({
    type: types.loaded,
    payload: { id, item, requestToken },
  }),
  errored: (id, error, requestToken) => ({
    type: types.errored,
    payload: { id, error, requestToken },
  }),
  removed: (id) => ({
    type: types.removed,
    payload: { id },
  }),
});

/**
 * Compose a reducer that applies the four shared cases on top of any
 * slice-specific state. Slices keep their own state shape and merge the
 * id-resource state under whatever keys they like; this helper just edits the
 * four sub-maps.
 *
 * The staleness guard lives in the reducer itself: a `loaded` or `errored`
 * action whose `requestToken` is older than what the store has seen for that
 * id is dropped on the floor.
 */
export const buildIdResourceReducer = <T, S extends IdResourceState<T>>(
  types: IdResourceActionTypes
): Reducer<S, AnyAction> => {
  return (state, action) => {
    if (!state) {
      // Reducers must be able to seed their own initial state; this helper
      // is only invoked once a slice supplies one.
      throw new Error("IdResource reducer requires an initial state.");
    }
    switch (action.type) {
      case types.startedLoading: {
        const { id, requestToken } = action.payload as {
          id: string;
          requestToken: number;
        };
        return {
          ...state,
          loadingFor: { ...(state.loadingFor ?? {}), [id]: true },
          errorFor: { ...(state.errorFor ?? {}), [id]: null },
          requestToken: { ...(state.requestToken ?? {}), [id]: requestToken },
        };
      }
      case types.loaded: {
        const { id, item, requestToken } = action.payload as {
          id: string;
          item: T;
          requestToken: number;
        };
        if ((state.requestToken?.[id] ?? 0) !== requestToken) {
          // Stale: a newer fetch superseded this one. Ignore the result.
          return state;
        }
        return {
          ...state,
          itemsById: { ...(state.itemsById ?? {}), [id]: item },
          loadingFor: { ...(state.loadingFor ?? {}), [id]: false },
          errorFor: { ...(state.errorFor ?? {}), [id]: null },
        };
      }
      case types.errored: {
        const { id, error, requestToken } = action.payload as {
          id: string;
          error: StoreError;
          requestToken: number;
        };
        if ((state.requestToken?.[id] ?? 0) !== requestToken) {
          return state;
        }
        return {
          ...state,
          loadingFor: { ...(state.loadingFor ?? {}), [id]: false },
          errorFor: { ...(state.errorFor ?? {}), [id]: error },
        };
      }
      case types.removed: {
        const { id } = action.payload as { id: string };
        const { [id]: _item, ...itemsById } = state.itemsById ?? {};
        const { [id]: _loading, ...loadingFor } = state.loadingFor ?? {};
        const { [id]: _error, ...errorFor } = state.errorFor ?? {};
        const { [id]: _token, ...requestToken } = state.requestToken ?? {};
        return { ...state, itemsById, loadingFor, errorFor, requestToken };
      }
      default:
        return state;
    }
  };
};

/**
 * Selectors for the four sub-maps. Slices pass a `pick` function so the
 * selector can find the IdResourceState block inside their RootState slice.
 */
export const buildIdResourceSelectors = <RootSlice, T>(
  pick: (slice: RootSlice) => IdResourceState<T>
) => ({
  selectItem: (id: string) => (slice: RootSlice): T | undefined =>
    pick(slice)?.itemsById?.[id],
  selectLoading: (id: string) => (slice: RootSlice): boolean =>
    Boolean(pick(slice)?.loadingFor?.[id]),
  selectError: (id: string) => (slice: RootSlice): StoreError | null =>
    pick(slice)?.errorFor?.[id] ?? null,
});

export type IdResourceSelectors<RootSlice, T> = ReturnType<
  typeof buildIdResourceSelectors<RootSlice, T>
>;

/**
 * Thunk helper that wraps the request-token + dispatch pattern.
 *
 *   1. Bump requestToken[id]; capture it locally.
 *   2. Dispatch startedLoading.
 *   3. Run `fetcher`. If it returns null, treat as "not found" but commit nothing.
 *   4. Dispatch loaded(item) on success or errored(err) on failure. The reducer
 *      is the one that drops stale results — by the time the result arrives,
 *      requestToken[id] may already point at a newer request.
 *
 * The thunk does NOT early-return when an item is already loaded. That was the
 * source of the original race; staleness is handled at commit-time instead.
 */
export const loadIdResource = <T, RootState>(
  id: string,
  fetcher: (assertActive: () => void) => Promise<T>,
  ctx: {
    actions: IdResourceActions<T>;
    selectSlice: (root: RootState) => IdResourceState<T>;
    requestScope?: string;
  }
) => async (
  dispatch: (action: AnyAction) => void,
  getState: () => RootState
): Promise<void> => {
  const slice = ctx.selectSlice(getState());
  // `?.` defends against tests / external callers that hand the thunk a slice
  // shape from before the id-resource fields were introduced. The reducer
  // initial state always has these maps, but callers supplying their own
  // initial state (test mocks, mainly) often don't.
  const token = (slice?.requestToken?.[id] ?? 0) + 1;
  dispatch(ctx.actions.startedLoading(id, token));
  try {
    const item = await readResource(getState, [
      ctx.actions.startedLoading(id, token).type, id, ctx.requestScope,
    ], fetcher);
    dispatch(ctx.actions.loaded(id, item, token));
  } catch (err) {
    const error =
      err instanceof StoreError
        ? err
        : new StoreError(
            err instanceof Error ? err.message : String(err)
          );
    dispatch(ctx.actions.errored(id, error, token));
  }
};
